/**
 * ProfilesPage — dedicated page for managing location profiles.
 *
 * Accessible from the user menu when authenticated.
 * Redirects to home if not authenticated.
 */

import { useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLayout from '../components/AppLayout';
import LocationProfiles from '../components/LocationProfiles';
import { useAuth } from '../hooks/useAuth';
import { useProfiles } from '../hooks/useProfiles';
import { usePreferences } from '../hooks/usePreferences';
import { LocationProfile } from '../types';

export default function ProfilesPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: isAuthLoading, login, logout } = useAuth();
  const { setRadius, setLastProfile, preferences } = usePreferences();
  const {
    profiles,
    isLoading: isProfilesLoading,
    createProfile,
    updateProfile,
    deleteProfile,
    isCreating,
    isUpdating,
    isDeleting,
  } = useProfiles(isAuthenticated);

  const handleProfileSelect = useCallback(
    (profile: LocationProfile) => {
      setRadius(profile.radiusKm);
      setLastProfile(profile.profileId);
    },
    [setRadius, setLastProfile]
  );

  const handleProfileDelete = useCallback(
    async (profileId: string) => {
      await deleteProfile(profileId);
      if (preferences.lastProfileId === profileId) {
        setRadius(10);
        setLastProfile(null);
      }
    },
    [deleteProfile, preferences.lastProfileId, setRadius, setLastProfile]
  );

  // Redirect unauthenticated users to home
  if (!isAuthLoading && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout
      user={user}
      isAuthenticated={isAuthenticated}
      isAuthLoading={isAuthLoading}
      onLogin={login}
      onLogout={logout}
    >
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              // Pass active profile coordinates to HomePage
              if (preferences.lastProfileId && profiles.length > 0) {
                const active = profiles.find((p) => p.profileId === preferences.lastProfileId);
                if (active) {
                  navigate(`/?lat=${active.coordinates.lat}&lng=${active.coordinates.lng}`);
                  return;
                }
              }
              navigate('/');
            }}
            size="small"
          >
            Takaisin
          </Button>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          Sijaintiprofiilit
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Hallitse sijaintiprofiileja. Valitse profiili asettaaksesi hakusijainnin ja -säteen.
        </Typography>

        <LocationProfiles
          profiles={profiles}
          isLoading={isProfilesLoading}
          activeProfileId={preferences.lastProfileId}
          onSelect={handleProfileSelect}
          onCreate={createProfile}
          onUpdate={updateProfile}
          onDelete={handleProfileDelete}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      </Box>
    </AppLayout>
  );
}
